import { useEffect, useState, useMemo, useCallback } from "react";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/utils-format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wrench } from "lucide-react";

const COST_FIELDS = [
  { key: "purchase_cost", label: "Purchase" },
  { key: "rental_cost",   label: "Rental" },
  { key: "maintenance_cost", label: "Maintenance" },
  { key: "fuel_cost",     label: "Fuel" },
  { key: "repair_cost",   label: "Repair" },
];

const blank = () => ({
  name: "", serial: "", purchase_date: "", condition: "Good",
  status: "Available", assigned: "", maintenance: "", notes: "",
  purchase_cost: 0, rental_cost: 0, maintenance_cost: 0, fuel_cost: 0, repair_cost: 0,
});

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  const load = useCallback(async () => { const r = await endpoints.list("equipment"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const t = { purchase: 0, rental: 0, maintenance: 0, fuel: 0, repair: 0 };
    items.forEach((e) => {
      t.purchase     += Number(e.purchase_cost     || 0);
      t.rental       += Number(e.rental_cost       || 0);
      t.maintenance  += Number(e.maintenance_cost  || 0);
      t.fuel         += Number(e.fuel_cost         || 0);
      t.repair       += Number(e.repair_cost       || 0);
    });
    t.grand = t.purchase + t.rental + t.maintenance + t.fuel + t.repair;
    return t;
  }, [items]);

  const startNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const startEdit = (e) => { setEditing(e); setForm({ ...blank(), ...e }); setOpen(true); };
  const save = async () => {
    const payload = { ...form };
    COST_FIELDS.forEach((c) => { payload[c.key] = Number(payload[c.key] || 0); });
    if (editing) await endpoints.update("equipment", editing.id, payload);
    else await endpoints.create("equipment", payload);
    toast.success("Equipment saved"); setOpen(false); load();
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await endpoints.remove("equipment", id); load(); };

  return (
    <Layout title="Equipment" subtitle={`${items.length} pieces · ${fmtMoney(totals.grand)} total expenses`}>
      <div className="flex justify-end mb-4"><Button onClick={startNew} data-testid="equipment-new"><Plus className="size-4 mr-1.5"/>New Equipment</Button></div>
      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="equipment-table">
          <thead className="bg-muted/40"><tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <th className="p-3 font-semibold">Equipment</th>
            <th className="p-3 font-semibold">Serial #</th>
            <th className="p-3 font-semibold">Purchased</th>
            <th className="p-3 font-semibold">Condition</th>
            <th className="p-3 font-semibold">Assigned</th>
            <th className="p-3 font-semibold text-right">Purchase</th>
            <th className="p-3 font-semibold text-right">Maint.</th>
            <th className="p-3 font-semibold text-right">Fuel</th>
            <th className="p-3 font-semibold text-right">Repair</th>
            <th className="p-3 font-semibold">Status</th>
            <th className="p-3 w-20"></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="11" className="p-10 text-center text-muted-foreground">No equipment yet.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} className="border-t border-border hover:bg-muted/30">
                <td className="p-3 font-semibold">{e.name}</td>
                <td className="p-3 font-mono text-xs">{e.serial}</td>
                <td className="p-3 text-xs">{fmtDate(e.purchase_date)}</td>
                <td className="p-3 text-xs">{e.condition}</td>
                <td className="p-3 text-xs">{e.assigned || "—"}</td>
                <td className="p-3 font-mono text-right">{fmtMoney(e.purchase_cost || 0)}</td>
                <td className="p-3 font-mono text-right">{fmtMoney(e.maintenance_cost || 0)}</td>
                <td className="p-3 font-mono text-right">{fmtMoney(e.fuel_cost || 0)}</td>
                <td className="p-3 font-mono text-right">{fmtMoney(e.repair_cost || 0)}</td>
                <td className="p-3"><StatusPill status={e.status}/></td>
                <td className="p-3"><div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="size-7" onClick={()=>startEdit(e)}><Pencil className="size-3.5"/></Button>
                  <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={()=>remove(e.id)}><Trash2 className="size-3.5"/></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      {/* Expense Summary Footer */}
      <div className="mt-6">
        <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Wrench className="size-4 text-primary"/>Equipment Expense Summary</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="equipment-totals">
          {[
            { label: "Total Purchase", value: totals.purchase },
            { label: "Total Rental", value: totals.rental },
            { label: "Total Maintenance", value: totals.maintenance },
            { label: "Total Fuel", value: totals.fuel },
            { label: "Total Repair", value: totals.repair },
          ].map((c) => (
            <div key={c.label} className="stat-card">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{c.label}</div>
              <div className="font-display text-lg font-bold mt-1 font-mono">{fmtMoney(c.value)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 p-4 rounded-xl bg-card border border-border flex items-center justify-between">
          <div className="font-display text-lg font-semibold">Grand Total Equipment Expenses</div>
          <div className="font-display text-2xl font-bold font-mono text-destructive">{fmtMoney(totals.grand)}</div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Equipment" : "New Equipment"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Serial #</Label><Input value={form.serial} onChange={(e)=>setForm({...form, serial:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={(e)=>setForm({...form, purchase_date:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Condition</Label>
              <Select value={form.condition} onValueChange={(v)=>setForm({...form, condition:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Excellent","Good","Fair","Poor"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Available","In Use","In Maintenance","Retired"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Assigned To</Label><Input value={form.assigned} onChange={(e)=>setForm({...form, assigned:e.target.value})}/></div>
            <div className="space-y-1.5 col-span-2"><Label>Maintenance Notes</Label><Input value={form.maintenance} onChange={(e)=>setForm({...form, maintenance:e.target.value})}/></div>
          </div>

          <div className="mt-4">
            <Label className="text-sm font-semibold mb-2 block">Equipment Expenses ($)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {COST_FIELDS.map((c) => (
                <div key={c.key} className="space-y-1.5">
                  <Label className="text-xs">{c.label}</Label>
                  <Input type="number" step="0.01" value={form[c.key] || 0} onChange={(e)=>setForm({...form, [c.key]: Number(e.target.value)})} data-testid={`eq-${c.key}`}/>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>

          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="equipment-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
